import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Heading } from '@ps/ds/components/primitives/Heading/Heading'
import { Separator } from '@ps/ds/components/primitives/Separator/Separator'
import { Card, CardContent } from '@ps/ds/ui/card'
import { TrackingWorkOrderProvider } from 'src/api/domains/service-tracking/serviceTracking.types'
import { GroupedRequestStatus } from 'src/api/types/requests'
import { NS } from 'src/constants/i18n'
import { CallServiceProviderItem } from 'src/features/contact/components/CallServiceProviderItem'
import { GenericContactGroup } from 'src/features/contact/components/GenericContactGroup'
import { PestShareContactAccordion } from 'src/features/contact/components/PestShareContactAccordion'
import { displayServiceProviderContact } from 'src/features/contact/utils/displayServiceProviderContact'

type ContactSectionProps = {
  groupedRequestStatus: GroupedRequestStatus
  provider?: TrackingWorkOrderProvider
}

export const ContactSection: FC<ContactSectionProps> = ({ groupedRequestStatus, provider }) => {
  const { t } = useTranslation(NS.TRACKING)

  const shouldDisplayServiceProviderContact = displayServiceProviderContact(
    groupedRequestStatus,
    provider,
  )

  return (
    <div className="flex flex-col gap-4">
      {/* MOBILE */}
      <div className="flex flex-col gap-8 md:hidden">
        <Heading as="h2" size="lg" weight="bold" className="sm:text-2xl">
          {t('contact.title')}
        </Heading>

        {shouldDisplayServiceProviderContact && provider ? (
          <div className="flex flex-col gap-4 md:hidden">
            <Card>
              <CardContent className="p-0">
                <CallServiceProviderItem provider={provider} />
              </CardContent>
            </Card>

            <PestShareContactAccordion />
          </div>
        ) : (
          <Card>
            <GenericContactGroup hasLongTitle />
          </Card>
        )}
      </div>

      {/* DESKTOP */}
      <div className="hidden md:flex md:flex-col">
        <Card>
          <CardContent className="p-0 pt-4 px-0">
            <Heading as="h2" size="lg" weight="bold" className="px-4 pb-6 sm:text-lg">
              {t('contact.title')}
            </Heading>

            {shouldDisplayServiceProviderContact && provider ? (
              <>
                <CallServiceProviderItem provider={provider} />
                <Separator />
                <GenericContactGroup hasLongTitle />
              </>
            ) : (
              <GenericContactGroup hasLongTitle />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
